import { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase/config';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, getDocs, collection } from 'firebase/firestore';
import { sendOTPEmail } from '../services/emailService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    // Check if user is logged in from localStorage
    const user = localStorage.getItem('currentUser');
    if (user) {
      setCurrentUser(JSON.parse(user));
    }
    setLoading(false);
  }, []);

  async function signup(email, password) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      // Create user document in Firestore with initial zero balances
      await setDoc(doc(db, "users", userCredential.user.uid), {
        email: email,
        createdAt: new Date().toISOString(),
        balance: 0,
        investments: [],
        transactions: [],
        status: 'active', // Set as active immediately
        role: 'user',
        accountDetails: {
          totalInvestment: 0,
          totalProfit: 0,
          availableBalance: 0,
          pendingWithdrawals: 0,
          lastDeposit: 0,
          lastWithdrawal: 0,
          accountLevel: 'standard'
        }
      });
      return userCredential;
    } catch (error) {
      throw error;
    }
  }

  async function login(email, password) {
    try {
      console.log('Attempting login for:', email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Authentication successful');
      
      const userDoc = await getDoc(doc(db, "users", userCredential.user.uid));
      console.log('User document fetched:', userDoc.exists());
      
      if (!userDoc.exists()) {
        console.error('User document not found in Firestore');
        throw new Error('User account not found.');
      }

      const userData = userDoc.data();
      console.log('User role:', userData.role);
      
      if (userData.status === 'suspended') {
        console.error('Account suspended');
        await signOut(auth);
        throw new Error('Your account has been suspended. Please contact support.');
      }

      // If user is admin, create session immediately without OTP
      if (userData.role === 'admin') {
        console.log('Admin login detected');
        const sessionData = {
          ...userCredential.user,
          ...userData,
          lastLoginAt: new Date().toISOString(),
          sessionStartedAt: new Date().toISOString(),
          sessionId: Math.random().toString(36).slice(2) + Date.now().toString(36)
        };

        setCurrentUser(sessionData);
        localStorage.setItem('currentUser', JSON.stringify(sessionData));
        localStorage.setItem('sessionStartedAt', new Date().toISOString());
        localStorage.setItem('sessionId', sessionData.sessionId);
        
        return { success: true, isAdmin: true };
      }

      // For regular users, return user data for OTP verification
      return {
        ...userCredential.user,
        ...userData
      };
    } catch (error) {
      console.error('Login error:', error.code, error.message);
      if (error.code === 'auth/invalid-credential') {
        throw new Error('Invalid email or password.');
      } else if (error.code === 'auth/user-not-found') {
        throw new Error('No account found with this email.');
      } else if (error.code === 'auth/wrong-password') {
        throw new Error('Incorrect password.');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many failed attempts. Please try again later.');
      } else {
        throw new Error(error.message || 'Failed to sign in. Please try again.');
      }
    }
  }

  async function verifyLoginOTP(userData, otp) {
    try {
      // Skip OTP verification for admin users
      if (userData.role === 'admin') {
        return true;
      }

      if (userData.securityDetails?.loginOTP !== otp) {
        throw new Error('Invalid OTP code');
      }

      // Create session with additional security measures
      const sessionData = {
        ...userData,
        lastLoginAt: new Date().toISOString(),
        sessionStartedAt: new Date().toISOString(),
        sessionId: Math.random().toString(36).slice(2) + Date.now().toString(36)
      };

      setCurrentUser(sessionData);
      localStorage.setItem('currentUser', JSON.stringify(sessionData));
      localStorage.setItem('sessionStartedAt', new Date().toISOString());
      localStorage.setItem('sessionId', sessionData.sessionId);
      
      return true;
    } catch (error) {
      throw error;
    }
  }

  async function verifyWithdrawalOTP(userId, otp) {
    try {
      const userDoc = await getDoc(doc(db, "users", userId));
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      
      if (userData.securityDetails?.withdrawalOTP !== otp) {
        throw new Error('Invalid withdrawal OTP');
      }

      return true;
    } catch (error) {
      throw error;
    }
  }

  function logout() {
    setCurrentUser(null);
    setUserRole(null);
    // Clear all session-related data
    localStorage.removeItem('currentUser');
    localStorage.removeItem('sessionStartedAt');
    localStorage.removeItem('sessionId');
    sessionStorage.clear();

    // Clear browser history state
    if (typeof window !== 'undefined') {
      // Replace the current history entry with login page
      window.history.replaceState(null, '', '/login');
      
      // Clear forward history if possible
      if (window.history.length > 0) {
        window.history.go(-(window.history.length - 1));
      }
    }

    return signOut(auth);
  }

  async function checkUserRole(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const role = userDoc.data().role || 'user';
        setUserRole(role);
        return role;
      }
      return 'user'; // default role
    } catch (error) {
      console.error('Error checking user role:', error);
      return 'user';
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && validateSession()) {
        await checkUserRole(user.uid);
        setCurrentUser(user);
      } else {
        setUserRole(null);
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Register new user
  const register = async (userData) => {
    try {
      // Create Firebase auth account immediately
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        userData.email,
        userData.password
      );

      // Generate OTP codes for the user
      const loginOTP = generateOTP('LOGIN');
      const withdrawalOTP = generateOTP('WDL');

      // Send OTP codes via email
      await sendOTPEmail(userData.email, loginOTP, withdrawalOTP);

      // Create user document in Firestore with registration data
      await setDoc(doc(db, "users", userCredential.user.uid), {
        fullName: userData.fullName,
        email: userData.email,
        country: userData.country,
        address: userData.address,
        mobileNumber: userData.mobileNumber,
        registrationDate: new Date().toISOString(),
        status: 'active',
        role: 'user',
        idCardUrl: userData.idCardUrl,
        accountDetails: {
          totalInvestment: 0,
          totalProfit: 0,
          availableBalance: 0,
          pendingWithdrawals: 0,
          lastDeposit: 0,
          lastWithdrawal: 0,
          accountLevel: determineInvestmentPlan(userData.amount)
        },
        investments: [],
        transactions: [],
        securityDetails: {
          loginOTP: loginOTP,
          withdrawalOTP: withdrawalOTP,
          otpCreatedAt: new Date().toISOString()
        }
      });

      // Create session data
      const sessionData = {
        ...userCredential.user,
        lastLoginAt: new Date().toISOString(),
        sessionStartedAt: new Date().toISOString(),
        sessionId: Math.random().toString(36).slice(2) + Date.now().toString(36)
      };

      // Set current user and session
      setCurrentUser(sessionData);
      localStorage.setItem('currentUser', JSON.stringify(sessionData));
      localStorage.setItem('sessionStartedAt', new Date().toISOString());
      localStorage.setItem('sessionId', sessionData.sessionId);

      return {
        success: true,
        user: userCredential.user,
        message: `Account created successfully! Your OTP codes have been sent to your email address. Please check your inbox and save the codes securely.`,
        otpCodes: {
          loginOTP,
          withdrawalOTP
        }
      };
    } catch (err) {
      setError('Failed to create an account. Please try again.');
      throw err;
    }
  };

  // Helper function to generate OTP codes
  const generateOTP = (prefix) => {
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = prefix;
    for (let i = 0; i < 6; i++) {
      code += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return code;
  };

  // Determine investment plan based on amount
  const determineInvestmentPlan = (amount) => {
    const amountNum = parseFloat(amount);
    if (amountNum >= 1000000) return 'advanced';
    if (amountNum >= 150000) return 'premium';
    return 'standard';
  };

  // Add password reset function
  async function resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return {
        success: true,
        message: 'Password reset email sent. Please check your inbox.'
      };
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw new Error('Failed to send password reset email. Please try again.');
    }
  }

  // Modify the validateSession function to be less aggressive
  const validateSession = () => {
    try {
      const sessionStartedAt = localStorage.getItem('sessionStartedAt');
      const currentUser = localStorage.getItem('currentUser');
      
      // If no session data exists, that's okay - just return true
      // This allows public pages to work without a session
      if (!sessionStartedAt || !currentUser) {
        return true;
      }

      const sessionStart = new Date(sessionStartedAt);
      const now = new Date();
      const sessionDuration = (now - sessionStart) / (1000 * 60 * 60); // in hours

      // Session expires after 24 hours
      if (sessionDuration > 24) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      return true; // On error, allow access rather than blocking
    }
  };

  // Modify the useEffect for session checking to be less frequent
  useEffect(() => {
    const handleFocus = () => {
      if (!validateSession()) {
        logout();
      }
    };

    // Only add focus listener if there's an active session
    if (localStorage.getItem('currentUser')) {
      window.addEventListener('focus', handleFocus);
    }
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Add function for admin to update user balances
  async function updateUserBalance(userId, balanceUpdates) {
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        ...balanceUpdates,
        lastUpdated: new Date().toISOString(),
        updatedBy: currentUser?.email || 'admin'
      });
      return true;
    } catch (error) {
      console.error('Error updating user balance:', error);
      throw error;
    }
  }

  // Add function to get user's financial data
  async function getUserFinancialData(userId) {
    try {
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists()) {
        return userDoc.data().accountDetails || {};
      }
      return null;
    } catch (error) {
      console.error('Error fetching user financial data:', error);
      throw error;
    }
  }

  // Add function to get all users (for admin)
  async function getAllUsers() {
    try {
      const usersSnapshot = await getDocs(collection(db, "users"));
      return usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  // Add function to update user's ID card URL
  async function updateUserIdCard(userId, idCardUrl) {
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        idCardUrl: idCardUrl,
        lastUpdated: new Date().toISOString()
      });
      return true;
    } catch (error) {
      console.error('Error updating user ID card:', error);
      throw error;
    }
  }

  const value = {
    currentUser,
    loading,
    error,
    userRole,
    login,
    logout,
    signup,
    isAdmin: userRole === 'admin',
    checkUserRole,
    register,
    resetPassword,
    validateSession,
    updateUserBalance,
    getUserFinancialData,
    getAllUsers,
    verifyLoginOTP,
    verifyWithdrawalOTP,
    updateUserIdCard
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 